"use client";

import Link from "next/link";
import Image, { type StaticImageData } from "next/image";
import type { ReactNode } from "react";
import { useState, useEffect } from "react";
import Lottie from "lottie-react";

import { cn } from "@/lib/utils";
import { processLottieForDarkMode } from "@/lib/utils/lottie-colors";
import { isAllowedIconLibraryPath, sanitizeSvg } from "@/lib/security/sanitize-svg";

type Props = {
	title: string;
	href?: string;
	image?: string | StaticImageData;
	icon?: ReactNode;
	iconClassName?: string;
	onClick?: () => void;
};

/** Same pulse treatment as `CategoryCard` (`CategoryIconPlaceholder`), sized for the nav row (28px). */
function NavCategoryIconPlaceholder() {
	return (
		<div className="flex size-7 items-center justify-center justify-self-center" aria-hidden>
			<span className="size-7 rounded-full bg-secondary/20 shadow-inner motion-safe:animate-pulse dark:bg-secondary/15" />
		</div>
	);
}

function LottieIcon({ path, alt }: { path: string; alt: string }) {
	const [animationData, setAnimationData] = useState<any>(null);
	const [error, setError] = useState(false);
	const [loading, setLoading] = useState(true);
	const [isDarkMode, setIsDarkMode] = useState(false);

	useEffect(() => {
		const checkDarkMode = () => {
			if (typeof window !== 'undefined') {
				return document.documentElement.classList.contains('dark') || 
				       document.documentElement.classList.contains('dark-mode');
			}
			return false;
		};

		setIsDarkMode(checkDarkMode());

		const observer = new MutationObserver(() => {
			setIsDarkMode(checkDarkMode());
		});

		if (typeof window !== 'undefined') {
			observer.observe(document.documentElement, {
				attributes: true,
				attributeFilter: ['class']
			});
		}

		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		let isMounted = true;
		setLoading(true);
		setError(false);
		setAnimationData(null);

		fetch(path)
			.then(res => {
				if (!res.ok) {
					throw new Error(`File not found: ${path}`);
				}
				return res.json();
			})
			.then(data => {
				if (isMounted) {
					const processedData = isDarkMode 
						? processLottieForDarkMode(data) 
						: data;
					setAnimationData(processedData);
					setLoading(false);
				}
			})
			.catch(() => {
				if (isMounted) {
					setError(true);
					setLoading(false);
				}
			});

		return () => {
			isMounted = false;
		};
	}, [path, isDarkMode]);

	if (loading) {
		return <NavCategoryIconPlaceholder />;
	}

	if (error || !animationData) {
		return (
			<div
				className="flex size-7 items-center justify-center justify-self-center rounded-full border border-dashed border-border/60 bg-muted/40 dark:bg-muted/25"
				title={`${alt} — animation unavailable`}
				aria-hidden
			/>
		);
	}

	return (
		<div className="flex size-7 items-center justify-center justify-self-center overflow-hidden">
			<Lottie
				animationData={animationData}
				loop={true}
				autoplay={true}
				style={{ width: 28, height: 28, display: 'block' }}
			/>
		</div>
	);
}

function SvgIcon({ src, alt }: { src: string; alt: string }) {
	const [svgContent, setSvgContent] = useState<string | null>(null);
	const [isDarkMode, setIsDarkMode] = useState(false);
	const [fetchDone, setFetchDone] = useState(false);

	useEffect(() => {
		const checkDarkMode = () => {
			if (typeof window !== 'undefined') {
				return document.documentElement.classList.contains('dark') || 
				       document.documentElement.classList.contains('dark-mode');
			}
			return false;
		};

		setIsDarkMode(checkDarkMode());

		const observer = new MutationObserver(() => {
			setIsDarkMode(checkDarkMode());
		});

		if (typeof window !== 'undefined') {
			observer.observe(document.documentElement, {
				attributes: true,
				attributeFilter: ['class']
			});
		}

		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		setFetchDone(false);
		setSvgContent(null);
		if (!isAllowedIconLibraryPath(src)) {
			setFetchDone(true);
			return;
		}
		fetch(src)
			.then(res => res.text())
			.then(svg => {
				if (isDarkMode) {
					let processed = svg
						.replace(/fill="#000000"/gi, 'fill="#07A8BC"')
						.replace(/fill="#232323"/gi, 'fill="#07A8BC"')
						.replace(/fill="#000"/gi, 'fill="#07A8BC"')
						.replace(/fill="rgb\(0,\s*0,\s*0\)"/gi, 'fill="#07A8BC"')
						.replace(/fill="rgba\(0,\s*0,\s*0[^)]*\)"/gi, 'fill="#07A8BC"')
						.replace(/stroke="#000000"/gi, 'stroke="#07A8BC"')
						.replace(/stroke="#232323"/gi, 'stroke="#07A8BC"')
						.replace(/stroke="#000"/gi, 'stroke="#07A8BC"')
						.replace(/stroke="rgb\(0,\s*0,\s*0\)"/gi, 'stroke="#07A8BC"')
						.replace(/stroke="rgba\(0,\s*0,\s*0[^)]*\)"/gi, 'stroke="#07A8BC"')
						.replace(/fill=#000000/gi, 'fill="#07A8BC"')
						.replace(/fill=#232323/gi, 'fill="#07A8BC"')
						.replace(/fill=#000/gi, 'fill="#07A8BC"')
						.replace(/stroke=#000000/gi, 'stroke="#07A8BC"')
						.replace(/stroke=#232323/gi, 'stroke="#07A8BC"')
						.replace(/stroke=#000/gi, 'stroke="#07A8BC"')
						.replace(/fill:\s*#000000/gi, 'fill: #07A8BC')
						.replace(/fill:\s*#232323/gi, 'fill: #07A8BC')
						.replace(/fill:\s*#000/gi, 'fill: #07A8BC')
						.replace(/fill:\s*rgb\(0,\s*0,\s*0\)/gi, 'fill: #07A8BC')
						.replace(/stroke:\s*#000000/gi, 'stroke: #07A8BC')
						.replace(/stroke:\s*#232323/gi, 'stroke: #07A8BC')
						.replace(/stroke:\s*#000/gi, 'stroke: #07A8BC')
						.replace(/stroke:\s*rgb\(0,\s*0,\s*0\)/gi, 'stroke: #07A8BC');
					
					processed = processed.replace(/([^#\w])#000000([^0-9a-fA-F])/gi, '$1#07A8BC$2');
					processed = processed.replace(/([^#\w])#232323([^0-9a-fA-F])/gi, '$1#07A8BC$2');
					processed = processed.replace(/([^#\w])#000([^0-9a-fA-F])/gi, '$1#07A8BC$2');
					
					setSvgContent(sanitizeSvg(processed));
				} else {
					setSvgContent(sanitizeSvg(svg));
				}
			})
			.catch(() => {
				setSvgContent(null);
			})
			.finally(() => {
				setFetchDone(true);
			});
	}, [src, isDarkMode]);

	if (!fetchDone) {
		return <NavCategoryIconPlaceholder />;
	}

	if (!svgContent) {
		return (
			<Image
				src={src}
				alt={alt}
				width={28}
				height={28}
				loading="lazy"
				unoptimized
				className="icon-library-icon size-7 justify-self-center object-contain"
			/>
		);
	}

	return (
		<div 
			className="icon-library-icon size-7 justify-self-center object-contain"
			dangerouslySetInnerHTML={{ __html: svgContent }}
		/>
	);
}

export default function NavDropdownItem({ title, href, image: logo, icon, iconClassName, onClick }: Props) {
	const isJsonIcon = logo && typeof logo === 'string' && logo.startsWith('/icon-library') && logo.endsWith('.json');
	const isSvgIcon = logo && typeof logo === 'string' && logo.startsWith('/icon-library') && !logo.endsWith('.json');
	const content = (
		<>
			{icon ? (
				<div className={cn("h-7 w-7 flex items-center justify-center justify-self-center", iconClassName)}>
					{icon}
				</div>
			) : isJsonIcon ? (
				<LottieIcon path={logo as string} alt={`${title} category icon`} />
			) : isSvgIcon ? (
				<SvgIcon src={logo as string} alt={`${title} category icon`} />
			) : (
				<Image
					src={logo!}
					alt={`${title} category icon`}
					width={28}
					height={28}
					quality={100}
					loading="lazy"
					sizes="32px"
					className={cn("h-7 w-7 object-contain justify-self-center", iconClassName)}
					draggable={false}
				/>
			)}
			<span className="pointer-events-none">{title}</span>
		</>
	);
	const className = "grid h-14 w-full grid-cols-[2rem_1fr] items-center gap-4 px-4 text-left font-light text-foreground transition-colors hover:bg-secondary/[0.04] hover:text-secondary";

	return (
		<li>
			{href ? (
				<Link href={href} className={className}>
					{content}
				</Link>
			) : (
				<button type="button" onClick={onClick} className={className}>
					{content}
				</button>
			)}
		</li>
	);
}
